import React, { useCallback, useMemo, useState } from "react";
import { Grid, Stack, Tooltip, Avatar as MuiAvatar } from "@mui/material";
import { createSvgIcon } from "@mui/material/utils";

import Avatar from "../../../../../../components/Avatar";
import CustomMenu from "../../../../../../components/Menu";
import CustomMenuItem from "../../../../../../components/Menu/CustomMenuItem";
import { CustomButton } from "../../../../../../components/Button/Button";
import { DeleteIcone } from "../../../../../../components/UI/Icons";
import { TooltipName } from "../../../../../../utils/Enums";
import { createAvatarName, hasImage } from "../../../../../../utils";

const PlusIcon = createSvgIcon(
  <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    strokeWidth={1.5}
    stroke="currentColor"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M12 4.5v15m7.5-7.5h-15"
    />
  </svg>,
  "Plus"
);

const getCollaboratorName = (collaborator) =>
  `${collaborator?.user?.first_name || ""} ${
    collaborator?.user?.last_name || ""
  }`.trim();

const getCollaboratorInitials = (collaborator) =>
  createAvatarName(getCollaboratorName(collaborator));

const getOptionUserId = (option) => option?.value ?? option?.user_id;

const getOptionUserName = (option) => option?.label ?? option?.username ?? "";

const Collaborators = ({
  cardId,
  cardCollabrator,
  collaboratorOptions,
  canAddCollaborator = false,
  updateCardmutate,
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [hoveredCollaboratorId, setHoveredCollaboratorId] = useState(null);
  const open = Boolean(anchorEl);

  const handleOpenMenu = useCallback((event) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  }, []);

  const handleCloseMenu = useCallback((event) => {
    event?.stopPropagation?.();
    setAnchorEl(null);
  }, []);

  const handleAddColabrotor = useCallback(
    (userId) => {
      updateCardmutate({
        card_id: cardId,
        data: {
          collaborator_user_id: userId,
        },
      });
      setAnchorEl(null);
    },
    [cardId, updateCardmutate]
  );

  const handleRemoveColabrotor = useCallback(
    (userId) => {
      updateCardmutate({
        card_id: cardId,
        data: {
          remove_collaborator_user_id: userId,
        },
      });
      setAnchorEl(null);
    },
    [cardId, updateCardmutate]
  );

  const collaborators = useMemo(
    () => (Array.isArray(cardCollabrator) ? cardCollabrator : []),
    [cardCollabrator]
  );

  const availableCollaborators = useMemo(
    () => (Array.isArray(collaboratorOptions) ? collaboratorOptions : []),
    [collaboratorOptions]
  );

  return (
    <div className="box">
      <Grid item className="allocated-hours">
        <p>Collaborators</p>
        <div className="taskdetail-sidebar-right-col">
          <div className="collaborators-detail">
            <Stack direction="row" spacing={1} className="collaborators-list">
              {collaborators.length > 0 ? (
                collaborators.map((collaborator) => {
                  const collaboratorName = getCollaboratorName(collaborator);

                  return (
                    <Avatar
                      key={`collaborator-${collaborator?.user_id}`}
                      username={collaboratorName}
                      user_image={collaborator?.user?.user_image}
                      title={collaboratorName}
                      className="card-avtar header-avatar collaborator-avatar"
                      style={
                        !hasImage(collaborator?.user?.user_image) && {
                          bgcolor:
                            collaborator?.user?.profile_color?.background,
                          color: collaborator?.user?.profile_color?.text,
                        }
                      }
                    >
                      {getCollaboratorInitials(collaborator)}
                    </Avatar>
                  );
                })
              ) : (
                <span className="show-none">None</span>
              )}

              {canAddCollaborator && (
                <Tooltip title={TooltipName.CARD_DETAIL.ADD_COLABRATOR}>
                  <CustomButton
                    className="reset-btn collaborator-menu-trigger"
                    onClick={handleOpenMenu}
                    sx={{ padding: 0, minWidth: 0 }}
                  >
                    <MuiAvatar className="add-user-avatar">
                      <PlusIcon />
                    </MuiAvatar>
                  </CustomButton>
                </Tooltip>
              )}
            </Stack>

            <CustomMenu
              anchorEl={anchorEl}
              open={open}
              handleClose={handleCloseMenu}
              className="collaborator-menu"
            >
              {collaborators.map((collaborator) => {
                const collaboratorName = getCollaboratorName(collaborator);

                return (
                  <CustomMenuItem
                    key={`selected-collaborator-${collaborator?.user_id}`}
                    onClick={() =>
                      handleRemoveColabrotor(collaborator?.user_id)
                    }
                    className="board-users-list-menu active-assign selected-border mb-2"
                    onMouseEnter={() =>
                      setHoveredCollaboratorId(collaborator?.user_id)
                    }
                    onMouseLeave={() => setHoveredCollaboratorId(null)}
                  >
                    <Avatar
                      username={collaboratorName}
                      user_image={collaborator?.user?.user_image}
                      title={collaboratorName}
                      className="user-avtar-dropdown user-avatar mr-8"
                      style={
                        !hasImage(collaborator?.user?.user_image) && {
                          bgcolor:
                            collaborator?.user?.profile_color?.background,
                          color: collaborator?.user?.profile_color?.text,
                        }
                      }
                    >
                      {getCollaboratorInitials(collaborator)}
                    </Avatar>
                    <div className="collaborator-menu-item-content">
                      <span>{collaboratorName}</span>
                    </div>
                    <div className="remove-user-delete-icon">
                      {hoveredCollaboratorId === collaborator?.user_id && (
                        <DeleteIcone height={"24px"} width={"24px"} />
                      )}
                    </div>
                  </CustomMenuItem>
                );
              })}

              {availableCollaborators.map((user) => (
                <CustomMenuItem
                  key={`available-collaborator-${getOptionUserId(user)}`}
                  onClick={() => handleAddColabrotor(getOptionUserId(user))}
                  className="board-users-list-menu"
                >
                  <Avatar
                    username={getOptionUserName(user)}
                    user_image={user?.user_image}
                    title={getOptionUserName(user)}
                    className="user-avtar-dropdown user-avatar mr-8"
                    style={
                      !hasImage(user?.user_image) && {
                        bgcolor: user?.profile_color?.background,
                        color: user?.profile_color?.text,
                      }
                    }
                  >
                    {createAvatarName(getOptionUserName(user))}
                  </Avatar>
                  <div className="collaborator-menu-item-content">
                    <span>{getOptionUserName(user)}</span>
                  </div>
                </CustomMenuItem>
              ))}

              {!collaborators.length && !availableCollaborators.length && (
                <CustomMenuItem disabled className="board-users-list-menu">
                  No collaborators available
                </CustomMenuItem>
              )}
            </CustomMenu>
          </div>
        </div>
      </Grid>
    </div>
  );
};

export default React.memo(Collaborators);
